import * as yup from 'yup';
import memoizee from 'memoizee';
import countries from 'resources/addresses';
import { RELATIONSHIPS } from './enum';
import { textAreaRegex, validatePostCode, validateTextSchema, ErrorMessage } from 'utils/commonSchema';

yup.setLocale({
  mixed: {
    oneOf: '',
    required: '',
  },
});

const textFieldMax = 50;
const textFieldMin = 4;
const textMin = 1;
const orgMax = 100;
const addressMax = 225;
const stateMax = 20;
const emailRegex =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const phoneNumberRegex =
  /^(([0-9]{6,20})|([+]?[(]?(([2-9][0-9])|([2-8][0-9][0-9]))[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}))$/;

const postalAddressErrorMsgMin = 'Remember to include your unit number as well as your street number';
export const relationshipWithPreferredContact = [
  'Agent',
  'Authorised Representative',
  'Broker',
  'Third Party Administrator',
  'Underwriting Agency',
];

export const isRelationshipNeedsPreferredContact = memoizee((relationship: string) =>
  relationshipWithPreferredContact.includes(relationship)
);

const policyOrClaimSchema = yup
  .string()
  .matches(textAreaRegex, ErrorMessage.ALPHA_NUMERIC)
  .min(textFieldMin, ErrorMessage.MIN_4_CHARACTER_MORE)
  .max(textFieldMax)
  .required(ErrorMessage.MIN_4_CHARACTER_MORE);

export const contactReasonSchemaObject = {
  policyOrClaim: yup.string().oneOf(['policy', 'claim']).required(),

  policyNumber: yup
    .string()
    .nullable()
    .when('policyOrClaim', {
      is: 'policy',
      then: () => policyOrClaimSchema,
    }),

  claimNumber: yup
    .string()
    .nullable()
    .when('policyOrClaim', {
      is: 'claim',
      then: () => policyOrClaimSchema,
    }),
};

export const contactReasonSchema = yup.object().shape(contactReasonSchemaObject);

const yourDetailsSchemaObject = {
  ...contactReasonSchemaObject,
  relationship: yup.string().oneOf(RELATIONSHIPS).required('Relationship is required'),

  firstName: validateTextSchema({ minLen: textMin, maxLen: textFieldMax, required: true }),
  lastName: validateTextSchema({ minLen: textMin, maxLen: textFieldMax, required: true }),
  organisation: validateTextSchema({ minLen: textMin, maxLen: orgMax, required: true }),
};

export const yourDetailsSchema = yup.object().shape(yourDetailsSchemaObject);
const contactDetailsSchemaObject = {
  emailAddress: yup
    .string()
    .matches(emailRegex, 'Please enter a valid email address')
    .max(orgMax)
    .required('Please enter a valid email address'),
  phoneNumber: yup
    .string()
    .matches(phoneNumberRegex, 'Please enter a valid phone number')
    .min(textFieldMin)
    .max(20)
    .required('Please enter a valid phone number'),

  contactType: yup
    .string()
    .nullable()
    .test('relationship_with_preferred_contact', 'Invalid contactType', function (contactType) {
      const { relationship } = this.parent;
      if (isRelationshipNeedsPreferredContact(relationship)) {
        const schema = yup.string().ensure().oneOf(['yes', 'no']).required();
        return schema.isValidSync(contactType);
      }
      return true;
    }),

  contactPreferedMethod: yup
    .string()
    .nullable()
    .test('relationship_with_preferred_contact', 'Invalid contactPreferedMethod', function (contactPreferedMethod) {
      const { relationship, contactType } = this.parent;
      if (isRelationshipNeedsPreferredContact(relationship) && contactType === 'yes') {
        const schema = yup.string().oneOf(['email', 'post', 'phone']).required();
        return schema.isValidSync(contactPreferedMethod);
      }
      return true;
    }),

  streetAddress: yup.string().when(['contactType', 'isManualAddressEnabled'], {
    is: (contactType: string, isManualAddressEnabled: boolean) => contactType === 'yes' && isManualAddressEnabled,
    then: () => validateTextSchema({ minLen: textMin, maxLen: addressMax, required: true }),
  }),

  suburb: yup.string().when(['contactType', 'isManualAddressEnabled'], {
    is: (contactType: string, isManualAddressEnabled: boolean) => contactType === 'yes' && isManualAddressEnabled,
    then: () => validateTextSchema({ minLen: textMin, maxLen: addressMax, required: true }),
  }),

  country: yup
    .string()
    .nullable()
    .when(['contactType', 'isManualAddressEnabled'], {
      is: (contactType: string, isManualAddressEnabled: boolean) => contactType === 'yes' && isManualAddressEnabled,
      then: () => yup.string().oneOf(countries, 'Country is a required field').required().label('Country'),
    }),

  state: yup.string().when(['contactType', 'isManualAddressEnabled'], {
    is: (contactType: string, isManualAddressEnabled: boolean) => contactType === 'yes' && isManualAddressEnabled,
    then: () => validateTextSchema({ minLen: textMin, maxLen: stateMax, required: true }),
  }),

  postalAddress: yup
    .string()
    .nullable()
    .when(['contactPreferedMethod', 'isManualAddressEnabled'], {
      is: (contactPreferedMethod: string, isManualAddressEnabled: boolean) =>
        contactPreferedMethod === 'post' && !isManualAddressEnabled,
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    }),
  isPostalAddressValid: yup.boolean().when(['contactPreferedMethod', 'isManualAddressEnabled'], {
    is: (contactPreferedMethod: string, isManualAddressEnabled: boolean) =>
      contactPreferedMethod === 'post' && !isManualAddressEnabled,
    then: () => yup.boolean().required().oneOf([true]),
  }),

  postcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode) {
    const { state, contactType, relationship, contactPreferedMethod, isManualAddressEnabled, country } = this.parent;

    if (contactType === 'no' || !isRelationshipNeedsPreferredContact(relationship)) {
      return true;
    }

    if (contactPreferedMethod === 'post' && isManualAddressEnabled && typeof postCode === 'string') {
      return validatePostCode(country)(state)(postCode);
    }

    return true;
  }),
};

export const contactDetailsSchema = yup.object().shape(contactDetailsSchemaObject);

const validationSchema = yup.object().shape({
  ...contactReasonSchemaObject,
  ...yourDetailsSchemaObject,
  ...contactDetailsSchemaObject,
});

export default validationSchema;
